import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Result, Button } from 'antd';
import { useAuth } from '@/hooks';

interface AdminGuardProps {
  children: ReactNode;
  requireSuperAdmin?: boolean;
}

/**
 * Route guard component that verifies admin authentication
 * Redirects to login if not authenticated or shows access denied if not admin
 */
export const AdminGuard = ({ children, requireSuperAdmin = false }: AdminGuardProps) => {
  const { user, loading, isAdmin, isSuperAdmin, signOut } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="Verifying access..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show access denied if user is not an admin
  if (!isAdmin) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You don't have permission to access the admin portal."
        extra={[
          <Button key="logout" type="primary" onClick={signOut}>
            Sign Out
          </Button>,
        ]}
      />
    );
  }

  // Show access denied if super admin is required but user is not super admin
  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Insufficient Permissions"
        subTitle="This action requires super admin privileges."
        extra={[
          <Button key="back" type="primary" onClick={() => window.history.back()}>
            Go Back
          </Button>,
        ]}
      />
    );
  }

  // User is authorized, render children
  return <>{children}</>;
};

export default AdminGuard;
